import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/modal.css';
import axiosInstance from '../../api/axiosInstance';
import { AccessibleSelect } from '../common';
import { formatDate } from '../../utils/dateTimeFormat';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const EditTestSuiteModal = ({ isOpen, onClose, onSaveTestSuite, onSaveSuccess, projects, testSuite }) => {
  const [testSuiteName, setTestSuiteName] = useState('');
  const [testSuiteDescription, setTestSuiteDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [error, setError] = useState(null);
  const [enableAutomation, setEnableAutomation] = useState(false);
  const [enableReporting, setEnableReporting] = useState(false);
  const [enableNotifications, setEnableNotifications] = useState(false);

  const projectOptions = useMemo(() => {
    if (!testSuite?.project_id) return [];
    return [
      {
        value: String(testSuite.project_id),
        label: testSuite.project_name || String(testSuite.project_id),
      },
    ];
  }, [testSuite]);

  // Initialize form with test suite data when it changes
  useEffect(() => {
    if (testSuite) {
      setTestSuiteName(testSuite.suite_name || '');
      setTestSuiteDescription(testSuite.description || '');
      setSelectedProject(testSuite.project_id ? testSuite.project_id.toString() : '');
      
      // Set checkbox states if they exist in the test suite object
      setEnableAutomation(testSuite.enable_automated_execution || false);
      setEnableReporting(testSuite.enable_reporting || false);
      setEnableNotifications(testSuite.enable_notification || false);
    }
  }, [testSuite]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!testSuiteName.trim()) {
      setError('Test suite name is required');
      return;
    }
    
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    
    // Create updated test suite object
    const updatedTestSuite = {
      ...testSuite,
      name: testSuiteName.trim(),
      description: testSuiteDescription.trim(),
      projectId: parseInt(selectedProject),
      updatedAt: new Date().toISOString(),
      enableAutomation,
      enableReporting,
      enableNotifications
    };
    updateSuites(updatedTestSuite)
    // Call the onSaveTestSuite callback with the updated test suite
    // onSaveTestSuite(updatedTestSuite);
    
    // Reset error
    setError(null);
  };
const updateSuites = async(updatedTestSuite) => {
  const projectsRes = await axiosInstance.put(
    `/test_suite/update/${updatedTestSuite.suite_id}`,
    null,
    {
      params: {
        suite_name: updatedTestSuite.name,
        project_id: updatedTestSuite.project_id,
        description: updatedTestSuite.description,
        enable_notification: updatedTestSuite.enable_notification,
        enable_automated_execution: updatedTestSuite.enable_automated_execution,
        enable_reporting: updatedTestSuite.enable_reporting,
      },
    }
  );
if(projectsRes){
  if (typeof onSaveSuccess === 'function') {
    onSaveSuccess(updatedTestSuite);
  } else {
    onClose();
  }
}
}
  if (!isOpen || !testSuite) return null;

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay edit-test-suite-modal-overlay" onClick={onClose}></div>
      <div className="modal edit-test-suite-modal">
        <div className="modal-header">
          <h2>Edit Test Suite</h2>
          <button className="modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <form className="edit-test-suite-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="test-suite-name">Test Suite Name</label>
              <input
                type="text"
                id="test-suite-name"
                placeholder="Enter test suite name"
                className="form-input"
                value={testSuiteName}
                onChange={(e) => setTestSuiteName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="test-suite-description">Description</label>
              <textarea
                id="test-suite-description"
                placeholder="Enter test suite description"
                className="form-input"
                rows="4"
                value={testSuiteDescription}
                onChange={(e) => setTestSuiteDescription(e.target.value)}
              ></textarea>
            </div>
            <div className="form-group">
              <label id="project-select-label" htmlFor="project-select">Project</label>
              <AccessibleSelect
                id="project-select"
                value={selectedProject}
                onChange={setSelectedProject}
                disabled={true}
                options={projectOptions}
                placeholder="Select a project"
                aria-labelledby="project-select-label"
              />
            </div>
            <div className="form-group">
              <label>Test Suite Settings</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={enableAutomation}
                    onChange={(e) => setEnableAutomation(e.target.checked)}
                  /> 
                  <span>Enable Automated Execution</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={enableReporting}
                    onChange={(e) => setEnableReporting(e.target.checked)}
                  /> 
                  <span>Enable Reporting</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={enableNotifications}
                    onChange={(e) => setEnableNotifications(e.target.checked)}
                  /> 
                  <span>Enable Notifications</span>
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Test Suite Statistics</label>
              <div className="stats-group">
                <div className="stat-item">
                  <span className="stat-label">Test Cases:</span>
                  <span className="stat-value">{testSuite.test_case_count || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Pass Rate:</span>
                  <span className="stat-value">{testSuite.average_pass_rate || 0}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Last Updated:</span>
                  <span className="stat-value">{formatDate(testSuite.updated_at)}</span>
                </div>
              </div>
            </div>
            {error && <div className="form-error">{error}</div>}
          </form>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>Save Changes</button>
        </div>
      </div>
    </>
  );
};

export default EditTestSuiteModal;
